/**
 * X(트위터) 글자 수 계산과 인용 공유 문구 조립.
 *
 * X 는 글자 수를 '가중치'로 센다 (twitter-text 기본 설정).
 * 아래 범위(라틴·숫자·일반 문장부호)는 1, 나머지는 2로 계산하고 상한은 280이다.
 * 한글은 2로 세므로 한국어만 쓰면 실질 140자다.
 */

const WEIGHT_ONE_RANGES = [
  [0x0000, 0x10ff],
  [0x2000, 0x200d],
  [0x2010, 0x201f],
  [0x2032, 0x2037]
];

export const MAX_TWEET_WEIGHT = 280;

/** 짧게 쓰자는 자체 목표치 (공백 포함 문자 수) */
export const TARGET_CHAR_LIMIT = 160;

/**
 * 두 기준을 함께 본다.
 * - charLimit(160): 우리 목표. 길어지면 읽히지 않는다
 * - weightLimit(280): X 의 실제 상한. 한글은 1자가 2라 140자에서 걸린다
 * 둘 중 먼저 걸리는 쪽이 실질 상한이다.
 */
export const measureTweet = (text = '') => {
  const chars = [...text].length;
  const weight = tweetWeight(text);
  const overWeight = weight > MAX_TWEET_WEIGHT;
  const overChars = chars > TARGET_CHAR_LIMIT;

  return {
    chars,
    charLimit: TARGET_CHAR_LIMIT,
    weight,
    weightLimit: MAX_TWEET_WEIGHT,
    overWeight,   // X 가 거부한다 — 반드시 막아야 한다
    overChars,    // 목표 초과 — 경고만
    blocked: overWeight
  };
};

const charWeight = (codePoint) =>
  WEIGHT_ONE_RANGES.some(([from, to]) => codePoint >= from && codePoint <= to) ? 1 : 2;

/** X 기준 가중치 길이 (한글 1자 = 2) */
export const tweetWeight = (text = '') =>
  [...text].reduce((sum, ch) => sum + charWeight(ch.codePointAt(0)), 0);

/** 남은 가중치 안에 들어가도록 잘라낸다 (자소가 쪼개지지 않게 코드포인트 단위) */
export const truncateToWeight = (text = '', budget) => {
  if (tweetWeight(text) <= budget) return text;

  let used = 0;
  let result = '';
  for (const ch of text) {
    const w = charWeight(ch.codePointAt(0));
    if (used + w > budget - 1) break; // 말줄임표 자리 1
    used += w;
    result += ch;
  }
  return `${result.trimEnd()}…`;
};

/** 인용 아래에 붙는 출처 줄 */
export const quoteSuffix = ({ bookTitle, author, page }) => {
  const source = [
    bookTitle && `— ${bookTitle}`,
    author && ` (${author})`,
    page ? ` p.${page}` : ''
  ].filter(Boolean).join('');

  return source ? `\n\n${source}` : '';
};

/** 인용 → 트윗 본문. 출처가 먼저 확보되고 남는 만큼 인용문이 들어간다. */
export const buildQuoteTweet = ({ text, bookTitle, author, page }) => {
  const suffix = quoteSuffix({ bookTitle, author, page });
  const budget = MAX_TWEET_WEIGHT - tweetWeight(suffix);
  const body = truncateToWeight((text || '').trim(), budget);

  return `${body}${suffix}`;
};

/**
 * 인용을 고를 수 있는 조각으로 나눈다.
 *
 * 상한을 넘는 인용을 뒤에서부터 잘라 주면, 잘린 부분이 사라져 되돌릴 수가 없다.
 * 조각으로 나눠 두면 어디를 남길지 직접 고를 수 있다.
 *
 * 줄바꿈과 문장부호로 먼저 나누고, 그래도 한 조각이 예산을 넘으면
 * 쉼표·구분점에서 한 번 더 쪼갠다 (한 문장이 통째로 긴 인용을 위해).
 */
export const splitQuoteSegments = (text = '', budget = MAX_TWEET_WEIGHT) => {
  const bySentence = String(text)
    .split(/\n+/)
    .flatMap(line => line.match(/[^.!?…]+[.!?…]*/g) || [line])
    .map(part => part.trim())
    .filter(Boolean);

  const pieces = bySentence.flatMap(part => {
    if (tweetWeight(part) <= budget) return [part];
    // 한 문장이 예산을 넘으면 절 단위로 한 번 더 나눈다
    return (part.match(/[^,;·—]+[,;·—]*/g) || [part])
      .map(clause => clause.trim())
      .filter(Boolean);
  });

  return pieces.map((segment, index) => ({ id: `s${index}`, text: segment }));
};

/**
 * 고른 조각을 이어 붙인다.
 * 중간을 건너뛰었으면 그 자리에 말줄임표를 넣는다 — 원문을 그대로 옮긴 것처럼
 * 보이면 안 되기 때문이다.
 */
export const joinQuoteSegments = (segments = [], selectedIds = []) => {
  const picked = new Set(selectedIds);
  const indexes = segments
    .map((segment, index) => (picked.has(segment.id) ? index : -1))
    .filter(index => index >= 0);

  if (indexes.length === 0) return '';

  return indexes
    .map((index, order) => {
      const gapBefore = order > 0 && index - indexes[order - 1] > 1;
      return `${gapBefore ? '… ' : ''}${segments[index].text}`;
    })
    .join(' ');
};

/** 앞에서부터 예산이 허락하는 만큼 고른다 (자동 축약과 같은 결과) */
export const selectWithinBudget = (segments = [], budget = MAX_TWEET_WEIGHT) => {
  const chosen = [];
  segments.forEach(segment => {
    const next = joinQuoteSegments(segments, [...chosen, segment.id]);
    if (tweetWeight(next) <= budget) chosen.push(segment.id);
  });
  // 첫 조각조차 넘치면 그거라도 담는다 (본문이 비면 고를 것이 없다)
  return chosen.length > 0 ? chosen : segments.slice(0, 1).map(s => s.id);
};

export const tweetIntentUrl = (text) =>
  `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;

/** 팝업으로 열되 차단되면 현재 탭으로 넘어간다 */
export const openTweetIntent = (text) => {
  const url = tweetIntentUrl(text);
  try {
    const width = 600;
    const height = 400;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    const win = window.open(url, 'shareQuote', `width=${width},height=${height},left=${left},top=${top}`);
    if (win) win.focus();
    else window.location.href = url;
  } catch {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  }
};
